import React, { useMemo } from 'react';
import { useIntl } from 'umi';
import classNames from 'classnames';
import moment from 'moment';
import dataFormat from '@home/utils/dbDataFormat/dataFormat';
import { LANGUAGE_EN } from '@home/_config/config';
import { DATA_TYPE_OBJECT, getName } from '@home/utils/common';
import GameRankIcons from '@home/pages/studio/casualHub/components/GameRankIcons';
import MetricsCardDod from '@/pages/dashboard/components/MetricsCardDod';
import Abnormal from '@/pages/dashboard/components/Abnormal';
import {
  CasualCard,
  CasualCardHeader,
  CasualCardMetric,
  CasualCardTrend,
  CasualCardBody,
  getDodValues,
  isCasualHubMultiMonthRange,
} from '../CasualCard';
import {
  ICasualSubMetricCardProps,
  IGameRankConfig,
  ISubtitleCardTimeItem,
  TSubtitleTimeApi,
} from './type';
import './index.scss';

/**
 * 后端 card_metrics 的 time 可能为 YYYY-MM-DD、YYYYMMDD（如 20260228）、YYYYMM 等；统一展示为 YYYY-MM-DD
 */
function formatMetricTimeForSubtitle(raw: unknown): string {
  if (raw === undefined || raw === null) return '';
  const t = String(raw).trim();
  const m = moment(
    t,
    ['YYYY-MM-DD', 'YYYY-MM-DD HH:mm:ss', 'YYYYMMDD', 'YYYYMM'],
    true,
  );
  return m.isValid() ? m.format('YYYY-MM-DD') : '';
}

function sliceDate10(raw: unknown): string {
  if (raw === undefined || raw === null) return '';
  return String(raw).trim().slice(0, 10);
}

const CasualSubMetricCard: React.FC<ICasualSubMetricCardProps> = (props) => {
  const { config, apiDataKey, data: propsData, className, filter: filterProp, changeData } = props;
  const {
    titleEn,
    titleCn,
    subtitleEn,
    subtitleCn,
    titleIconUrl,
    metricKey,
    subtitleTimePrefixEn = 'Last Updated',
    subtitleTimePrefixCn = '数据更新至',
    subtitleTimeMetricKey,
    formatRule,
    preUnit,
    gameRanks: configGameRanks,
    useDataGameRanks,
    link: gameRankLinkTemplate,
    gameRankJumpType,
    useDynamicSubtitle,
    subtitleMode = 'metricTime',
    subtitleTimeApiDataKey,
    ytmSubtitleMainMetricKey,
    ytmSubtitleSubMetricEndKeys,
    subMetrics,
    trendConfig,
    trendTitleEn,
    trendTitleCn,
    trendTimeType,
    isRightCard = false,
    useChangeOnMultiMonth = true,
    compareMetrics,
    trendLastDays,
    subtitleChangeKey,
  } = config || {};

  const filterVal =
    filterProp ??
    ((propsData as Record<string, unknown> | undefined)?.filter as
      | Record<string, unknown>
      | undefined);

  const isMultiMonthRange = useMemo(
    () => isCasualHubMultiMonthRange(filterVal),
    [filterVal],
  );

  /** 与 StudioMetricCardTrend 一致：新窗口打开 dashboard 时通过 query 继承日期 */
  const gameRankWindowParams = useMemo(() => {
    const startTime = filterVal?.start_time;
    const endTime = filterVal?.end_time;
    const dataType = filterVal?.date_type as string | undefined;
    const timeType =
      (dataType && DATA_TYPE_OBJECT[dataType]) || 'Month';
    const payType = filterVal?.payType;
    return {
      start: startTime,
      end: endTime,
      timeType,
      ...(payType ? { payment_type: payType } : {}),
    };
  }, [filterVal]);

  const { locale } = useIntl();
  const isEn = locale === LANGUAGE_EN;

  const apiData = (propsData as Record<string, Record<string, unknown>>)?.[apiDataKey] || {};
  const { data, loading } = apiData as { data: Record<string, unknown>; loading: boolean; };
  const { trend_list: trendList, card_list: cardList } = (data || {}) as {
    trend_list: Record<string, unknown>[];
    card_list: Record<string, unknown>[];
  };

  const casualInfo = (propsData as Record<string, unknown>)?.casualInfo as Record<string, unknown> | undefined;

  const resolvedGameRanks = useMemo<IGameRankConfig[]>(() => {
    if (!useDataGameRanks) return configGameRanks || [];
    const dataRanks = (data as Record<string, unknown>)?.gameRanks as Array<Record<string, unknown>> | undefined;
    if (!dataRanks?.length) return configGameRanks || [];
    const games = casualInfo?.games as Array<{ game_name: string; cover?: string; }> | undefined;
    const gameMap = new Map<string, string>();
    if (games?.length) {
      games.forEach(g => {
        gameMap.set(g.game_name, g.cover || '');
      });
    }
    return dataRanks.map(item => ({
      iconUrl: gameMap.get(String(item.gameCode || '')) || gameMap.get(String(item.name || '')) || '',
      rank: item.rank as number,
      name: String(item.name || ''),
      gameCode: String(item.gameCode || item.name || ''),
    }));
  }, [useDataGameRanks, configGameRanks, data, casualInfo]);

  const subtitleApiEntry = subtitleTimeApiDataKey
    ? (propsData as Record<string, unknown>)?.[subtitleTimeApiDataKey]
    : undefined;

  React.useEffect(() => {
    if (!subtitleChangeKey || !changeData) return;
    const cardListToSearch: Record<string, unknown>[] | undefined = subtitleTimeApiDataKey
      ? (subtitleApiEntry as TSubtitleTimeApi)?.data?.card_list
      : cardList;
    const timeKey = subtitleTimeMetricKey ?? metricKey;
    const card = cardListToSearch?.find(
      (c: Record<string, unknown>) => c.metrics_name === timeKey,
    ) as Record<string, unknown> | undefined;
    changeData(subtitleChangeKey, card?.time ? formatMetricTimeForSubtitle(card.time) : '');
  }, [subtitleChangeKey, cardList, subtitleTimeMetricKey, metricKey, subtitleApiEntry]);

  const dynamicSubtitle = useMemo(() => {
    if (!useDynamicSubtitle) return '';

    const mainCardForDataCheck = cardList?.find(
      (c: Record<string, unknown>) => c?.metrics_name === metricKey,
    ) as Record<string, unknown> | undefined;
    const hasData =
      mainCardForDataCheck?.value !== undefined &&
      mainCardForDataCheck?.value !== null &&
      String(mainCardForDataCheck?.value).trim() !== '';
    if (!hasData) return '';

    if (subtitleTimeApiDataKey) {
      const entry = propsData
        ? (propsData as Record<string, unknown>)[subtitleTimeApiDataKey]
        : undefined;
      const subtitleCardList = (entry as TSubtitleTimeApi)?.data?.card_list;
      const timeKey = subtitleTimeMetricKey ?? metricKey;
      const timeCard = subtitleCardList?.find(
        (c: ISubtitleCardTimeItem) => c?.metrics_name === timeKey,
      );
      const fromMetricTime = formatMetricTimeForSubtitle(timeCard?.time);
      if (fromMetricTime) {
        return `${getName(
          isEn,
          subtitleTimePrefixEn,
          subtitleTimePrefixCn,
        )}: ${fromMetricTime}`;
      }
      return '';
    }

    if (subtitleMode === 'filterRange') {
      const st = sliceDate10(filterVal?.start_time);
      const et = sliceDate10(filterVal?.end_time);
      if (st && et) return `${st}~${et}`;
      if (et) return et;
      if (st) return st;
      return '';
    }

    if (subtitleMode === 'filterEndWithPrefix') {
      const et = sliceDate10(filterVal?.end_time);
      if (!et) return '';
      return `${getName(isEn, subtitleTimePrefixEn, subtitleTimePrefixCn)}: ${et}`;
    }

    if (subtitleMode === 'ytmSelfIncubation') {
      const mainKey = ytmSubtitleMainMetricKey ?? metricKey;
      const mainCard = cardList?.find(
        (c: Record<string, unknown>) => c?.metrics_name === mainKey,
      ) as Record<string, unknown> | undefined;
      const apiTime = formatMetricTimeForSubtitle(mainCard?.time);
      const st = sliceDate10(filterVal?.start_time);
      if (st) return apiTime ? `${st}~${apiTime}` : st;
      return '';
    }

    const d = (data as Record<string, unknown>) || {};
    const en = d.subtitleEn as string | undefined;
    const cn = d.subtitleCn as string | undefined;
    if (
      (en !== undefined && en !== null && String(en).trim() !== '') ||
      (cn !== undefined && cn !== null && String(cn).trim() !== '')
    ) {
      return getName(isEn, en || '', cn || '');
    }
    const fromData = (d.subtitle as string) || '';
    if (fromData) return fromData;
    const timeKey = subtitleTimeMetricKey ?? metricKey;
    const timeCard = cardList?.find(
      (c: Record<string, unknown>) => c?.metrics_name === timeKey,
    ) as Record<string, unknown> | undefined;
    const fromMetricTime = formatMetricTimeForSubtitle(timeCard?.time);
    if (fromMetricTime) {
      return `${getName(isEn, subtitleTimePrefixEn, subtitleTimePrefixCn)}: ${fromMetricTime}`;
    }
    return '';
  }, [
    useDynamicSubtitle,
    subtitleMode,
    filterVal?.start_time,
    filterVal?.end_time,
    data,
    isEn,
    cardList,
    metricKey,
    subtitleTimeMetricKey,
    subtitleTimePrefixEn,
    subtitleTimePrefixCn,
    ytmSubtitleMainMetricKey,
    subtitleTimeApiDataKey,
    propsData,
  ]);

  const computedSubtitleByMetricKey = useMemo(() => {
    if (!useDynamicSubtitle || subtitleMode !== 'ytmSelfIncubation') return undefined;
    const keys = ytmSubtitleSubMetricEndKeys ?? ['cs_self_incubation_avg_dau'];
    const et = sliceDate10(filterVal?.end_time);
    if (!et) return undefined;
    const m: Record<string, string> = {};
    keys.forEach(k => {
      m[k] = et;
    });
    return m;
  }, [
    useDynamicSubtitle,
    subtitleMode,
    ytmSubtitleSubMetricEndKeys,
    filterVal?.end_time,
  ]);

  const subtitleByMetricKey = useMemo(() => {
    const fromData = (data as Record<string, unknown> | undefined)?.subtitleByMetricKey as
      | Record<string, string>
      | undefined;
    if (!computedSubtitleByMetricKey) return fromData;
    return { ...computedSubtitleByMetricKey, ...fromData };
  }, [data, computedSubtitleByMetricKey]);

  const resolvedSubtitleEn = dynamicSubtitle || subtitleEn;
  const resolvedSubtitleCn = dynamicSubtitle || subtitleCn;

  const cardData = cardList?.find(
    (card: Record<string, unknown>) => card?.metrics_name === metricKey
  ) as Record<string, unknown> | undefined;

  const dodValues = getDodValues(cardData, {
    isMultiMonthRange: isMultiMonthRange && useChangeOnMultiMonth,
    compareMetrics,
  });

  const componentMetrics = [
    {
      metricKey: trendConfig?.metricKey || metricKey,
      nameEn: trendConfig?.nameEn || 'Revenue',
      nameCn: trendConfig?.nameCn || 'Revenue',
      formatRule: 'thousands2Decimal',
      isCompare: trendConfig?.isCompare || false,
      compareToolTipNameEn: trendConfig?.compareToolTipNameEn,
      compareToolTipNameCn: trendConfig?.compareToolTipNameCn,
    },
  ];

  return (
    <CasualCard className={classNames('dbh-casual-sub-metric-card', className)}>
      <Abnormal
        isLoading={loading !== false}
        isNoData={false}
        className="dbh-casual-card__abnormal"
        component={
          <>
            <CasualCardHeader
              titleEn={titleEn}
              titleCn={titleCn}
              subtitleEn={resolvedSubtitleEn}
              subtitleCn={resolvedSubtitleCn}
              titleIconUrl={titleIconUrl}
              operation={resolvedGameRanks && resolvedGameRanks.length > 0 ? <GameRankIcons games={resolvedGameRanks} link={gameRankLinkTemplate} windowParams={gameRankWindowParams} jumpType={gameRankJumpType} /> : undefined}
            />
            <CasualCardBody>
              <div className='dbh-casual-card__metric-wrapper'>
                <CasualCardMetric
                  value={cardData?.value as string | number}
                  formatRule={formatRule}
                  preUnit={preUnit}
                  dodData={dodValues}
                />
                <div style={{ marginLeft: 'auto' }}>{resolvedGameRanks && resolvedGameRanks.length > 0 ? <GameRankIcons games={resolvedGameRanks} link={gameRankLinkTemplate} windowParams={gameRankWindowParams} jumpType={gameRankJumpType} /> : undefined}</div>
              </div>
              <div className="dbh-casual-card__sub-metrics">
                {subMetrics?.map((item) => {
                  const subCard = cardList?.find(
                    (c: Record<string, unknown>) => c?.metrics_name === item.metricKey
                  ) as Record<string, unknown> | undefined;

                  const subDodValues = getDodValues(subCard, {
                    isMultiMonthRange: isMultiMonthRange && useChangeOnMultiMonth,
                    compareMetrics: item?.compareMetrics ?? compareMetrics,
                  });
                  const label = getName(isEn, item.labelEn, item.labelCn);
                  const subDynamicSubtitle = item.useDynamicSubtitle
                    ? (subtitleByMetricKey?.[item.metricKey] ?? dynamicSubtitle)
                    : '';
                  const subSubtitle = subDynamicSubtitle || getName(isEn, item.subtitleEn, item.subtitleCn);
                  const formattedValue = dataFormat({
                    value: subCard?.value as string | number,
                    formatRule: item.formatRule,
                    fillZero: true,
                    emptyValue: '-',
                  });

                  return (
                    <div key={item.metricKey} className="dbh-casual-card__sub-item">
                      <div className='dbh-casual-card__sub-item__title-value-wrapper'>
                        <div className="dbh-casual-card__sub-label-row">
                          <span className="dbh-casual-card__sub-label">{label}</span>
                          {subSubtitle && (
                            <span className="dbh-casual-card__sub-subtitle">{subSubtitle}</span>
                          )}
                        </div>
                        <div className="dbh-casual-card__sub-value">
                          {subCard?.value && item.preUnit !== undefined && item.preUnit !== ''
                            ? String(item.preUnit)
                            : null}
                          {formattedValue}
                          {subCard?.value && item.endUnit !== undefined && item.endUnit !== ''
                            ? String(item.endUnit)
                            : null}
                        </div>
                      </div>
                      <MetricsCardDod data={subDodValues} />
                    </div>
                  );
                })}
              </div>

              <CasualCardTrend
                trendTitleEn={trendTitleEn}
                trendTitleCn={trendTitleCn}
                trendList={trendLastDays && trendList?.length > trendLastDays ? trendList.slice(-trendLastDays) : trendList}
                componentMetrics={componentMetrics}
                isRightCard={isRightCard}
                trendTimeType={trendTimeType}
              />
            </CasualCardBody>
          </>
        }
      />
    </CasualCard>
  );
};

export default CasualSubMetricCard;
